import type { UseCase } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Filter, Frown, MessagesSquare } from "lucide-react";
import { type ReactNode, useState } from "react";
import { PainKindBadge, PointsDelta, SuccessRate } from "@/components/domain/analytics";
import { DeltaPct } from "@/components/domain/score";
import { QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { EconomicsCard, fmtRoi, UseCaseEconomicsTable } from "@/features/agents/agent-economics";
import { PtsDelta, Segmented } from "@/features/analytics/bits";
import { FunnelTable } from "@/features/analytics/funnel-table";
import { PATHS, sessionSearch } from "@/features/analytics/links";
import { OutcomeLegend, OutcomeMixChart } from "@/features/analytics/outcome-chart";
import { KpiTile } from "@/features/overview/kpi-tile";
import { PathFlow } from "@/features/paths/path-flow";
import { type UseCaseAction, UseCaseDialogs } from "@/features/use-cases/use-case-actions";
import { UseCaseTree } from "@/features/use-cases/use-case-tree";
import { q } from "@/lib/api";
import { fmtCompact, fmtCost, fmtInt, fmtMoney, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/agents/$agentId/analytics")({
  component: AgentAnalyticsTab,
});

function CardLink({ to, search, children }: { to: string; search?: Record<string, string | number>; children: ReactNode }) {
  return (
    <Link to={to} search={search} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
      {children} <ArrowRight className="size-3" />
    </Link>
  );
}

function AgentAnalyticsTab() {
  const { agentId } = Route.useParams();
  const outcomes = useQuery(q.outcomes({ agentId }));
  const economics = useQuery(q.economics());
  const econ = economics.data?.find((e) => e.agentId === agentId);
  const [mode, setMode] = useState<"share" | "count">("share");

  return (
    <div className="flex flex-col gap-4">
      <section aria-label="Key metrics" className="@container">
        <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-3 @5xl:grid-cols-6">
          <KpiTile
            label="Sessions · 30d"
            to={PATHS.sessions}
            search={{ agentId }}
            value={econ ? fmtCompact(econ.sessions30d) : <Skeleton className="h-6 w-14" />}
            sub={econ ? `${fmtCompact(econ.successes30d)} resolved or converted` : " "}
          />
          <KpiTile
            label="Success rate · 7d"
            value={outcomes.data ? <SuccessRate value={outcomes.data.successRate} /> : <Skeleton className="h-6 w-14" />}
            sub={
              outcomes.data ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <PtsDelta value={outcomes.data.successRateDelta} /> vs previous 7 days
                </span>
              ) : (
                " "
              )
            }
          />
          <KpiTile
            label="Verification gap"
            value={outcomes.data ? fmtPct(outcomes.data.verificationGap.rate, 1) : <Skeleton className="h-6 w-14" />}
            sub={outcomes.data ? `${fmtInt(outcomes.data.verificationGap.contradicted)} of ${fmtInt(outcomes.data.verificationGap.claimedResolved)} "resolved" contradicted later` : " "}
          />
          <KpiTile label="Cost per success" value={econ ? fmtCost(econ.costPerSuccess) : <Skeleton className="h-6 w-14" />} sub="model + tool cost" />
          <KpiTile label="Value delivered · 30d" value={econ ? fmtMoney(econ.value, { compact: true }) : <Skeleton className="h-6 w-14" />} sub={econ ? <span className="line-clamp-1" title={econ.valueModel}>{econ.valueModel}</span> : " "} />
          <KpiTile label="Net · 30d" value={econ ? fmtMoney(econ.net, { compact: true }) : <Skeleton className="h-6 w-14" />} sub={econ ? `ROI ${fmtRoi(econ.roi)} after expected loss and premium` : " "} />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="flex flex-col lg:col-span-7">
          <CardHeader
            title="Outcome mix"
            description="Daily outcomes for this agent's sessions."
            actions={
              <Segmented
                size="sm"
                label="Outcome mix scale"
                value={mode}
                onChange={setMode}
                options={[
                  { value: "share", label: "Share" },
                  { value: "count", label: "Sessions" },
                ]}
              />
            }
          />
          <CardBody className="flex flex-1 flex-col gap-3 pb-3">
            <QueryState query={outcomes} loading={<Skeleton className="h-[232px]" />}>
              {(o) => (
                <>
                  <OutcomeMixChart daily={o.daily} mode={mode} height={232} />
                  <OutcomeLegend daily={o.daily} />
                </>
              )}
            </QueryState>
          </CardBody>
        </Card>
        <div className="lg:col-span-5">
          <QueryState query={economics} loading={<Skeleton className="h-full min-h-72" />}>
            {() => (econ ? <EconomicsCard e={econ} className="h-full" /> : <Card className="h-full"><EmptyState title="No economics yet" description="Add an outcome definition with a value to see what this agent delivers." /></Card>)}
          </QueryState>
        </div>
      </div>

      <AgentUseCases agentId={agentId} />

      <div className="grid gap-4 lg:grid-cols-12">
        <AgentFunnels agentId={agentId} />
        <AgentPainPoints agentId={agentId} />
      </div>

      <AgentPaths agentId={agentId} />

      {econ?.byUseCase.length ? (
        <Card>
          <CardHeader title="Economics by use case · 30d" description="Where this agent creates value and where it carries risk. High expected loss relative to value is a candidate for a platform control rather than for growth." />
          <UseCaseEconomicsTable e={econ} />
        </Card>
      ) : null}
    </div>
  );
}

function AgentUseCases({ agentId }: { agentId: string }) {
  const useCases = useQuery(q.useCases({ agentId }));
  const agents = useQuery(q.agents());
  const [target, setTarget] = useState<{ action: UseCaseAction; useCase: UseCase } | null>(null);
  return (
    <Card>
      <CardHeader title="Use cases" description="What users come to this agent for, by volume (7d)." actions={<CardLink to="/use-cases" search={{ agentId }}>Use-case map</CardLink>} />
      <QueryState query={useCases} loading={<Skeleton className="m-4 h-56" />}>
        {(list) => (
          <>
            <UseCaseTree useCases={list} agentNames={new Map(agents.data?.map((a) => [a.id, a.name]))} onAction={(action, useCase) => setTarget({ action, useCase })} />
            <UseCaseDialogs target={target} all={list} onClose={() => setTarget(null)} />
          </>
        )}
      </QueryState>
    </Card>
  );
}

function AgentFunnels({ agentId }: { agentId: string }) {
  const funnels = useQuery(q.funnels());
  return (
    <Card className="lg:col-span-7">
      <CardHeader title="Funnels" description="Funnels this agent's sessions take part in." actions={<CardLink to={PATHS.funnels}>All funnels</CardLink>} />
      <QueryState query={funnels} loading={<Skeleton className="m-4 h-32" />}>
        {(list) => {
          const mine = list.filter((f) => f.agentIds.includes(agentId));
          return mine.length ? <FunnelTable funnels={mine} /> : <EmptyState icon={<Filter />} title="Not in any funnel" description="Define a funnel that includes this agent to track conversion step by step." />;
        }}
      </QueryState>
    </Card>
  );
}

function AgentPainPoints({ agentId }: { agentId: string }) {
  const pains = useQuery(q.painPoints({ agentId }));
  return (
    <Card className="lg:col-span-5">
      <CardHeader title="Top pain points" description="Ranked by success impact × volume." actions={<CardLink to={PATHS.painPoints}>All pain points</CardLink>} />
      <QueryState query={pains} loading={<Skeleton className="m-4 h-32" />}>
        {(list) => {
          const ranked = [...list].filter((p) => p.status !== "dismissed" && p.status !== "resolved").sort((a, b) => a.successImpact * a.sessions7d - b.successImpact * b.sessions7d).slice(0, 5);
          return ranked.length ? (
            <ul className="flex flex-col">
              {ranked.map((p) => (
                <li key={p.id} className="flex flex-col gap-1 px-4 py-2.5 [&:not(:last-child)]:border-b">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-[13px] font-medium text-fg">{p.title}</span>
                    <PainKindBadge kind={p.kind} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 text-xs text-fg-3">
                    <span className="tabular">
                      <span className="text-fg-2">{fmtInt(p.sessions7d)}</span> sessions <DeltaPct value={p.deltaPct} invert className="ml-0.5 text-[11px]" />
                    </span>
                    <span>
                      success <PointsDelta value={p.successImpact} />
                    </span>
                    <Link to={PATHS.sessions} search={sessionSearch({ painPointId: p.id, agentId })} className="ml-auto hover:text-accent hover:underline">
                      Sessions
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Frown />} title="No open pain points" description="Nothing recurring is hurting this agent's sessions right now." />
          );
        }}
      </QueryState>
    </Card>
  );
}

function AgentPaths({ agentId }: { agentId: string }) {
  const paths = useQuery(q.paths({ agentId }));
  return (
    <Card>
      <CardHeader
        title="Conversation paths"
        description="This agent's discovered stages and where sessions end. Color is the success rate of each transition."
        actions={
          <>
            <Button variant="ghost" size="sm" asChild>
              <Link to={PATHS.sessions} search={sessionSearch({ agentId })}>
                <MessagesSquare /> Sessions
              </Link>
            </Button>
            <CardLink to="/paths" search={{ agentId }}>
              Golden vs failing paths
            </CardLink>
          </>
        }
      />
      <CardBody>
        <QueryState query={paths} loading={<Skeleton className="h-[320px]" />}>
          {(p) => <PathFlow paths={p} title="Conversation flow for this agent" />}
        </QueryState>
      </CardBody>
    </Card>
  );
}
